import React from 'react';
import { TempSpinner } from '@shared/components/TempSpinner';
import { GuestRegistryRouter } from './GuestRegistry.routes';
import { Route, Switch } from '@react-router';
import { useGuestRegistryController, GuestRegistryState } from './GuestRegistry.controller';
import { GuestRegistry as GuestRegistryList } from './routes/GuestRegistry';
import { GuestRegistryProvider as ReactProvider } from './state';
import { Purchase } from './routes/Purchase';
import { RegistryFontFaces } from '@apps/registry/common/util/fonts';
import { TelemetryProvider, useGuestRegistryTelemetry } from './GuestRegistry.telemetry';
import { RegistryProtected } from './routes/GuestRegistry/components/RegistryProtected';
import { ErrorSplash } from '@shared/components';
import { RegistryHidden } from './routes/GuestRegistry/components';
import { DropshipPurchaseConfirmationV2 } from './routes/GuestRegistry/components/DropshipPurchaseConfirmation';
import { usePurchaseContextIdFromLocation } from '../common/utils/dropship';
import { ShoppingCart } from './routes/GuestRegistry/components/ShoppingCart';
import { JoyKitThemeProvider } from '@withjoy/joykit';
import { StoredGiftReservationFieldsProvider } from './routes/GuestRegistry/components/StoredGiftReservationFieldsProvider';
import { PurchaseConfirmationModalProvider } from '@apps/registry/guest/routes/Purchase/components/PurchaseConfirmationModal/PurchaseConfirmationModal.provider';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { ShoppingCartProvider } from '@apps/registry/guest/routes/GuestRegistry/components/ShoppingCart/state';
import { useRegistryHost } from './utils/useRegistryHost';
import { NoSsr } from '@shared/components/NoSsr';
import { EventPageType, useGuestEventDetailsByNameQuery } from '@graphql/generated';
import { GuestRegistryHead } from './GuestRegistryHead';

interface GuestRegistryProps {
  eventHandle: string;
}

type PurchaseRouteSwitchProps = Readonly<{
  eventHandle: string;
  registry: GuestRegistryState;
  isRegistryHidden: boolean;
}>;

const PurchaseRouteSwitch = (props: PurchaseRouteSwitchProps) => {
  const purchaseContextId = usePurchaseContextIdFromLocation();

  if (purchaseContextId) {
    // `pcId` is present after user goes through stripe payment flow and is redirected
    return <DropshipPurchaseConfirmationV2 registry={props.registry} />;
  } else {
    // Default to the existing purchase confirmation page.
    return (
      <Purchase
        isAdmin={props.registry.isAdmin}
        eventId={props.registry.eventId}
        eventHandle={props.eventHandle}
        registry={props.registry.registry}
        shippingAddress={props.registry.shippingAddress}
        isRegistryHidden={props.isRegistryHidden}
      />
    );
  }
};

const GuestRegistry: React.FC<GuestRegistryProps> = props => {
  const { registry, loading, routes, location, canAccessPage, eventId, error } = useGuestRegistryController(props.eventHandle);
  const { trackError } = useGuestRegistryTelemetry();
  const isMobileOrTablet = useIsMobileOrTablet();

  const host = useRegistryHost();

  let content: JSX.Element | null = null;

  if (loading || (registry && !host)) {
    content = <TempSpinner />;
  } else if (!registry) {
    content = null;
  } else if (error) {
    trackError('guestRegistry', error);
    content = <ErrorSplash />;
  } else if (canAccessPage) {
    const sessionEmail = registry?.registry?.filter(registry => registry.id === eventId)[0]?.mySession?.email;
    content = (
      <PurchaseConfirmationModalProvider>
        <ShoppingCartProvider coupleAddress={registry.shippingAddress} eventHandle={props.eventHandle} eventId={eventId}>
          <TelemetryProvider context={{ eventId: registry.firebaseId, section: '' }}>
            <Switch location={location}>
              <Route
                exact={true}
                path={routes.guestRegistry.path}
                render={() => <GuestRegistryList eventHandle={props.eventHandle} eventId={eventId!} registryState={registry} />}
              />
              <Route
                exact={true}
                path={routes.purchase.path}
                render={() => {
                  return <PurchaseRouteSwitch eventHandle={props.eventHandle} registry={registry} isRegistryHidden={!!registry.isRegistryPageDisabled} />;
                }}
              />

              {/* TODO: extract out contribute route */}
              <Route
                exact={true}
                path={routes.contribute.path}
                render={() => (
                  <StoredGiftReservationFieldsProvider sessionEmail={sessionEmail}>
                    <Purchase
                      eventHandle={props.eventHandle}
                      registry={registry?.registry}
                      eventId={registry?.eventId}
                      isAdmin={registry.isAdmin}
                      shippingAddress={registry.shippingAddress}
                      isRegistryHidden={!!registry.isRegistryPageDisabled}
                      layoutOption={'donationFund'}
                    />
                  </StoredGiftReservationFieldsProvider>
                )}
              />
              {/* For native apps */}
              <Route
                exact={true}
                path={routes.shoppingCart.path}
                render={() => (
                  <>
                    {!isMobileOrTablet ? (
                      <GuestRegistryList eventHandle={props.eventHandle} eventId={eventId!} registryState={registry} />
                    ) : (
                      <StoredGiftReservationFieldsProvider sessionEmail={sessionEmail}>
                        <ShoppingCart
                          eventId={eventId!}
                          isShoppingPage
                          registry={registry.registry}
                          orders={registry.orders}
                          shippingAddress={registry.shippingAddress}
                          registryCurrencyCode={registry.registryCurrencyCode}
                        />
                      </StoredGiftReservationFieldsProvider>
                    )}
                  </>
                )}
              />
            </Switch>
          </TelemetryProvider>
        </ShoppingCartProvider>
      </PurchaseConfirmationModalProvider>
    );
  } else if (registry.isRegistryPageDisabled) {
    content = <RegistryHidden website={props.eventHandle} />;
  } else if (registry.isRegistryPagePasswordProtected) {
    content = <RegistryProtected eventId={eventId} eventHandle={props.eventHandle} />;
  }

  return <JoyKitThemeProvider>{content}</JoyKitThemeProvider>;
};

export const GuestRegistryProvider: React.FC<GuestRegistryProps> = props => {
  const { data } = useGuestEventDetailsByNameQuery({
    variables: {
      eventHandle: props.eventHandle,
      pageType: EventPageType.registry
    },
    batchMode: 'fast',
    ssr: true,
    fetchPolicy: 'cache-first'
  });
  const event = data?.eventByName;

  return (
    <>
      {event && (
        <GuestRegistryHead
          eventDisplayName={event.info?.eventDisplayName}
          imageUrl={event.pageInfo?.page.photo?.url || event.photo?.url}
          handle={event.website}
          ownerFirstName={event.info.ownerFirstName}
          ownerLastName={event.info.ownerLastName}
          fianceeFirstName={event.info.fianceeFirstName}
          pageData={event.pages}
          eventType={event.info.eventType}
        />
      )}

      <NoSsr>
        <RegistryFontFaces />
        <GuestRegistryRouter eventHandle={props.eventHandle}>
          <ReactProvider>
            <GuestRegistry {...props} />
          </ReactProvider>
        </GuestRegistryRouter>
      </NoSsr>
    </>
  );
};
